import uniq from 'lodash/uniq'
import type { FavoriteResponse } from '@kira-dancer/nuxt-common/types'
import { FavoriteGamesAction } from '@kira-dancer/nuxt-common/enums'
import type { GameItemExtend, CasinoGameItemExtend, FavoriteStatus } from '~/types/game'
import {
  LOBBY_GAME_LIMIT,
  TIME_DELAY_HIDDEN_MODAL,
  CURRENT_PAGED,
  LOBBY_GAME_MOBILE_LIMIT,
} from '~/constants'
import { FAVORITE_STATUS } from '~/resources/game'

const isOpenAlertFavoriteGame = ref(false)
const statusFavorite = reactive<FavoriteStatus>({
  class: '',
  message: '',
})
const favoriteCurrentPage = ref(CURRENT_PAGED)
export function useCustomFavoriteGame() {
  const { isLogged } = useUser()
  const { onToggleFavoriteGames, favoriteGameNames } = useFavoriteGames()
  const { allCasinoGames, allGames, fetchAllCasinoGames } = useFetchGames()
  const currentAllCasinoAndGames = reactive<GameItemExtend[]>([])
  const { isMobile } = useDevice()

  const limitPerPage = computed(() => {
    return isMobile ? LOBBY_GAME_MOBILE_LIMIT : LOBBY_GAME_LIMIT
  })
  const isLoading = ref(true)

  const getGameName = (item: GameItemExtend | CasinoGameItemExtend): string => {
    return `${item.provider}_${
      item.gameId ?? item.tableId ?? item.tableCode ?? item.ID
    }_${item.title}`
  }

  const allLobbyAndCasinoGames = computed(() => {
    const allItems: GameItemExtend[] =
      favoriteMappingGameNames.value.map((item) => {
        const itemGame = allGames?.value?.find((game) =>
          item === normalizeVietnameseWords(getGameName(game)),
        ) as GameItemExtend
        if (itemGame) {
          return { ...itemGame, favorite: true }
        }
        const itemCasino = allCasinoGames?.value?.find((game) =>
          item === normalizeVietnameseWords(getGameName(game)),
        ) as GameItemExtend
        if (itemCasino) {
          return { ...itemCasino, isCasino: true, favorite: true }
        }
        return {}
      }) || []
    return allItems
  })

  const allFavoriteGames = computed((): GameItemExtend[] => {
    const offset: number = (favoriteCurrentPage.value - 1) * limitPerPage.value
    const itemPerPage: number = Math.min(
      offset + limitPerPage.value,
      totalResults.value,
    )
    if (isMobile) {
      return allLobbyAndCasinoGames.value
    }
    return allLobbyAndCasinoGames.value.slice(offset, itemPerPage)
  })

  const totalResults = computed(() => {
    return allLobbyAndCasinoGames.value?.length
  })

  const isEmpty = computed(() => totalResults.value === 0 && !isLoading.value)

  const isShowPagination = computed(() => {
    return totalResults.value > limitPerPage.value
  })

  const favoriteMappingGameNames = computed((): string[] => {
    return uniq(
      favoriteGameNames?.value?.map((item: FavoriteResponse) => {
        return normalizeVietnameseWords(
          getGameName({ ...item, title: item.gameName }),
        )
      }) ?? []
    )
  })

  watch(
    () => favoriteGameNames.value,
    (value: FavoriteResponse[]) => {
      const totalPerPages = Math.ceil(value.length / limitPerPage.value)
      if (value.length && totalPerPages) {
        favoriteCurrentPage.value =
          favoriteCurrentPage.value > totalPerPages
            ? totalPerPages
            : favoriteCurrentPage.value
      }
    },
    { immediate: true, deep: true },
  )

  const fetchAllCasinoAndGames = async (): Promise<void> => {
    isLoading.value = true
    try {
      await fetchAllCasinoGames()
    } catch (error) {
      console.error('Failed to fetch all casino games', error)
    } finally {
      isLoading.value = false
    }
  }

  const isCheckFavorite = (
    item: GameItemExtend | CasinoGameItemExtend,
  ): boolean => {
    return favoriteMappingGameNames.value?.includes(
      normalizeVietnameseWords(getGameName(item)),
    )
  }

  // Set get, add, delete favorite Games
  const handleFavoriteGames = async (
    item: GameItemExtend | CasinoGameItemExtend,
  ): Promise<void> => {
    const payload = {
      provider: item.provider ?? '',
      gameId:
        item.gameId ??
        item.tableId ??
        item.tableCode ??
        item.ID?.toString() ??
        '',
      gameName: item.title ?? '',
    }
    const actionName = isCheckFavorite(item)
      ? FavoriteGamesAction.DELETE
      : FavoriteGamesAction.ADD
    const status = isCheckFavorite(item)
      ? FAVORITE_STATUS.DELETE
      : FAVORITE_STATUS.ADD
    try {
      await onToggleFavoriteGames(payload, actionName)
      Object.assign(statusFavorite, status)
    } catch (errors) {
      const message = (errors as { message: string }).message
      Object.assign(statusFavorite, { ...FAVORITE_STATUS.FAIL, message })
    } finally {
      if (isLogged.value) {
        isOpenAlertFavoriteGame.value = true
      }
      await sleep(TIME_DELAY_HIDDEN_MODAL)
      isOpenAlertFavoriteGame.value = false
    }
  }

  const changePage = (page: number) => {
    favoriteCurrentPage.value = page
    scrollToTop()
  }

  return {
    isCheckFavorite,
    handleFavoriteGames,
    changePage,
    fetchAllCasinoAndGames,
    isLoading,
    currentAllCasinoAndGames,
    isEmpty,
    totalResults,
    isShowPagination,
    favoriteCurrentPage,
    LOBBY_GAME_LIMIT,
    isOpenAlertFavoriteGame,
    statusFavorite,
    limitPerPage,
    allFavoriteGames,
  }
}
